/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react"
import { Evento } from "../service/ReprisesService"
import { getEventStatus } from "../utils/ReprisesUtils"
import { motion } from "framer-motion"
import { Icon } from "@/components/Atoms/Icon"
import { Calendar, MoreVertical, Layers, Edit3, Trash2, AlertTriangle } from "lucide-react"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"

interface RepriseCardProps {
  item: Evento
  index: number
  onOpen: () => void
  onEdit?: (item: Evento) => void
  onDelete?: (item: Evento) => Promise<void> // Alterado para Promise para lidar com loading se necessário
}

export const RepriseCard = ({ item, index, onOpen, onEdit, onDelete }: RepriseCardProps) => {
  const { user } = useAuth()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { expired, label, isUrgent } = getEventStatus(item.limiteExibicao)
  const hasSecondary = !!item.linkSegundoConteudo

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      await onDelete(item)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <>
      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <AlertDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
      >
        <AlertDialogContent className="border-white/10 bg-zinc-900 text-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 text-rose-500">
              <AlertTriangle size={24} />
              <AlertDialogTitle className="text-xl font-black uppercase italic">
                Excluir Conteúdo?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2 text-zinc-400">
              Você está prestes a remover o conteúdo: <br />
              <span className="font-bold text-white">"{item.descricao}"</span>.
              <br />
              <br />
              Esta ação é permanente e o vídeo não estará mais disponível para os usuários.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-zinc-800">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-rose-600 font-bold uppercase tracking-widest text-white hover:bg-rose-700"
            >
              Sim, excluir agora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: (index % 4) * 0.05 }}
        className={`group relative flex flex-col overflow-hidden rounded-[24px] border bg-zinc-900/40 transition-all duration-500 ${
          expired
            ? "border-white/5 opacity-70 grayscale"
            : "cursor-pointer border-white/10 shadow-lg hover:border-primary/50 hover:bg-zinc-900/80 hover:shadow-primary/5"
        }`}
      >
        {/* DROPDOWN DE GESTÃO - Sempre visível para ADMIN */}
        {user?.gestaoApp && (
          <div className="absolute right-3 top-3 z-30">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="flex size-9 items-center justify-center rounded-full border border-white/20 bg-zinc-950/80 text-white shadow-xl backdrop-blur-md transition-all hover:bg-primary hover:text-black active:scale-90"
                >
                  <MoreVertical size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 border-white/10 bg-zinc-900 p-2 text-white shadow-2xl"
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit?.(item)
                  }}
                  className="cursor-pointer gap-3 rounded-lg py-2.5 focus:bg-white/5 focus:text-white"
                >
                  <Edit3
                    size={16}
                    className="text-primary"
                  />
                  <span className="text-[11px] font-bold uppercase tracking-wider">
                    Editar Conteúdo
                  </span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsDeleteModalOpen(true) // Abre a modal em vez de deletar direto
                  }}
                  className="cursor-pointer gap-3 rounded-lg py-2.5 text-red-500 focus:bg-red-500/10 focus:text-red-500"
                >
                  <Trash2 size={16} />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Excluir</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Thumbnail Area */}
        <div
          className="relative aspect-video overflow-hidden bg-zinc-800"
          onClick={() => !expired && onOpen()}
        >
          {item.linkCapa ? (
            <img
              src={item.linkCapa}
              alt=""
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
              <Icon
                name="play"
                size={40}
                className="text-zinc-700 opacity-50"
              />
            </div>
          )}

          {/* Badge de Status */}
          <div className="absolute left-3 top-3 z-10">
            <span
              className={`rounded-lg border px-2.5 py-1 text-[9px] font-black uppercase tracking-wider backdrop-blur-md ${
                expired
                  ? "border-red-100/40 bg-red-500/50 text-red-100"
                  : isUrgent
                    ? "border-amber-100/40 bg-amber-500/50 text-amber-100"
                    : "border-emerald-100/40 bg-emerald-500/50 text-emerald-100"
              }`}
            >
              {label}
            </span>
          </div>

          {/* Indicador de 2 Conteúdos */}
          {hasSecondary && !expired && (
            <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/20 px-2 py-1 text-[9px] font-black uppercase tracking-tight text-primary backdrop-blur-md">
              <Layers size={10} />2 Conteúdos
            </div>
          )}

          {!expired && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <div className="flex size-14 items-center justify-center rounded-full bg-primary text-black shadow-xl ring-4 ring-primary/20">
                <Icon
                  name="play"
                  size={24}
                />
              </div>
            </div>
          )}
        </div>

        {/* Info Content */}
        <div
          className="flex flex-1 flex-col p-6"
          onClick={() => !expired && onOpen()}
        >
          <span className="mb-2 text-[9px] font-black uppercase tracking-[0.2em] text-primary">
            {item.agrupador ? item.agrupador.replace(/_/g, " ") : "GERAL"}
          </span>

          <h3 className="mb-6 line-clamp-2 text-base font-bold leading-tight text-zinc-100 transition-colors group-hover:text-primary">
            {item.descricao}
          </h3>

          <div className="mt-auto flex items-center gap-2 border-t border-white/5 pt-4 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
            <Calendar
              size={14}
              className="text-zinc-600"
            />
            <span>
              Expira em:{" "}
              <span className={isUrgent ? "text-amber-500" : "text-zinc-300"}>
                {item.limiteExibicao
                  ? format(new Date(item.limiteExibicao), "dd 'de' MMMM", { locale: ptBR })
                  : "A definir"}
              </span>
            </span>
          </div>
        </div>
      </motion.div>
    </>
  )
}
